import React from 'react';
import { FiArrowRight, FiCalendar, FiClock, FiEdit2, FiMapPin } from 'react-icons/fi';
import { Link } from '@tanstack/react-router';
import {
  getGigClosingTimeLabel,
  getGigStatusPresentation,
  isGigActive,
  isApplicationOpen,
  isApplicationNotYetOpen,
  canUserApplyToGig,
} from '../../utils/gigDate';
import { normalizeGigTagLabels } from '../../utils/gigTags';
import { getProfileIdentifier } from '../../utils/profileUrl';
import { Avatar } from '../ui/Avatar';

export interface GigItem {
  id: string;
  gig_uuid?: string;
  created_at?: string;
  owner_id?: number | string;
  title: string;
  description: string;
  location_name: string;
  // Authenticated API: nested owner object
  owner?: {
    id?: string | number;
    username?: string;
    name?: string;
    full_name?: string;
    prof_pics?: string | string[] | null;
    profile_picture?: string | null;
    profile_image?: string | null;
    avatar?: string | null;
    image?: string | null;
  } | null;
  // Public API: nested creator object
  creator?: {
    id?: string | number;
    username?: string;
    name?: string;
    full_name?: string;
    profile_pic?: string | null;
    profile_picture?: string | null;
    avatar?: string | null;
  } | null;
  // Top-level fields the public API may return instead of a nested owner object
  poster_name?: string | null;
  user_name?: string | null;
  creator_name?: string | null;
  username?: string | null;
  name?: string | null;
  full_name?: string | null;
  poster_image?: string | null;
  user_image?: string | null;
  creator_image?: string | null;
  poster_avatar?: string | null;
  user_avatar?: string | null;
  location?: {
    latitude?: number;
    longitude?: number;
    street?: string | null;
    state?: string;
    city?: string;
    country?: string;
  } | null;
  budget_type: 'flat_rate' | 'range' | string;
  budget_min?: string | null;
  budget_max?: string | null;
  budget_amount?: string | null;
  start_datetime: string;
  end_datetime: string;
  timezone?: string;
  application_start_datetime?: string | null;
  application_end_datetime?: string | null;
  can_apply?: boolean | string | number | null;
  canApply?: boolean | string | number | null;
  status: string;
  event_type?: string | null;
  image?: string | null;
  tags?: string[];
}

interface GigCardProps {
  gig: GigItem;
  onViewDetails: (gig: GigItem) => void;
  onApply: (gig: GigItem) => void;
  hideApply?: boolean;
  isOwner?: boolean;
  onEdit?: (gig: GigItem) => void;
}

function formatDateTime(iso?: string, wallClock = false): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...(wallClock ? { timeZone: 'UTC' } : {}),
  }).format(date);
}

function formatBudget(gig: GigItem): string | null {
  if (gig.budget_type === 'flat_rate') {
    const amount = gig.budget_amount ?? gig.budget_min ?? gig.budget_max;
    if (amount) return `$${parseFloat(amount).toLocaleString()} flat`;
  }
  if (gig.budget_type === 'range') {
    const min = gig.budget_min ? `$${parseFloat(gig.budget_min).toLocaleString()}` : null;
    const max = gig.budget_max ? `$${parseFloat(gig.budget_max).toLocaleString()}` : null;
    if (min && max && min !== max) return `${min} \u2013 ${max}`;
    if (min) return `From ${min}`;
    if (max) return `Up to ${max}`;
  }
  return null;
}

function getToneClass(tone: 'positive' | 'negative' | 'neutral'): string {
  if (tone === 'positive') return 'bg-green-500/10 border-green-500/30 text-green-400';
  if (tone === 'negative') return 'bg-red-500/10 border-red-500/30 text-red-400';
  return 'bg-zinc-800/80 border-white/10 text-zinc-300';
}

function getPostedTime(createdAt?: string): string {
  if (!createdAt) return 'Recently';

  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return 'Recently';

  const diff = Date.now() - created;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);

  if (seconds < 60) return 'Just now';
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  if (days < 7) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  if (weeks < 4) return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;

  return formatDateTime(createdAt) || 'Recently';
}

function getGigOwnerIdentifier(gig: GigItem): string | null {
  const ownerId = gig.creator?.id ?? gig.owner?.id ?? gig.owner_id;
  const username = gig.creator?.username ?? gig.owner?.username ?? gig.username ?? undefined;
  const identifier = getProfileIdentifier({
    id: ownerId,
    username,
  }).trim();
  return identifier || null;
}

function getOwnerAvatar(gig: GigItem): string | null {
  // Public API uses `creator.profile_pic`; authenticated API uses `owner.*`
  const directAvatar =
    gig.creator?.profile_pic ||
    gig.creator?.profile_picture ||
    gig.creator?.avatar ||
    gig.owner?.profile_picture ||
    gig.owner?.profile_image ||
    gig.owner?.avatar ||
    gig.owner?.image ||
    gig.poster_image ||
    gig.user_image ||
    gig.creator_image ||
    gig.poster_avatar ||
    gig.user_avatar ||
    null;
  if (directAvatar) return directAvatar;

  const profilePics = gig.owner?.prof_pics;
  if (!profilePics) return null;
  if (typeof profilePics === 'string') return profilePics;
  if (Array.isArray(profilePics) && typeof profilePics[0] === 'string')
    return profilePics[0] ?? null;
  return null;
}

function getOwnerDisplayName(gig: GigItem): string {
  // Public API uses `creator`; authenticated API uses `owner`
  const ownerName =
    gig.creator?.full_name ||
    gig.creator?.name ||
    gig.creator?.username ||
    gig.owner?.full_name ||
    gig.owner?.name ||
    gig.owner?.username ||
    gig.poster_name ||
    gig.user_name ||
    gig.creator_name ||
    gig.full_name ||
    gig.name ||
    gig.username ||
    null;
  // Only show 'Anonymous' as a last resort — never hide a real name
  return ownerName?.trim() || 'Anonymous';
}

export const GigCard: React.FC<GigCardProps> = ({
  gig,
  onViewDetails,
  onApply,
  hideApply = false,
  isOwner = false,
  onEdit,
}) => {
  const statusPresentation = getGigStatusPresentation(gig, isOwner);
  const budgetDisplay = formatBudget(gig);
  const gigExpired = !isGigActive(gig);
  const appOpen = isApplicationOpen(gig);
  const canApply = !isOwner && !hideApply && canUserApplyToGig(gig);
  const applicationNotYetOpen =
    !isOwner && !hideApply && !gigExpired && isApplicationNotYetOpen(gig);
  const applicationClosed =
    !isOwner && !hideApply && !gigExpired && !appOpen && !applicationNotYetOpen;
  const closingLabel = canApply ? getGigClosingTimeLabel(gig) : null;
  const ownerIdentifier = getGigOwnerIdentifier(gig);
  const ownerAvatar = getOwnerAvatar(gig);
  const ownerDisplayName = getOwnerDisplayName(gig);
  const postedTime = getPostedTime(gig.created_at);
  const gigTagLabels = normalizeGigTagLabels(gig.tags);

  return (
    <div
      className="relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-[#0e0e10] cursor-pointer transition-all duration-200 hover:border-zinc-600 hover:shadow-xl hover:shadow-black/60"
      onClick={() => onViewDetails(gig)}
    >
      {/* Expired overlay stripe */}
      {gigExpired && (
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center gap-1.5 bg-red-950/80 border-b border-red-800/50 px-3 py-1.5 backdrop-blur-sm">
          <FiClock size={11} className="text-red-400 shrink-0" />
          <span className="text-red-400 text-[11px] font-semibold uppercase tracking-widest">
            Expired
          </span>
        </div>
      )}
      {/* Application not yet open stripe */}
      {!gigExpired && applicationNotYetOpen && (
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center gap-1.5 bg-amber-950/80 border-b border-amber-800/50 px-3 py-1.5 backdrop-blur-sm">
          <FiClock size={11} className="text-amber-400 shrink-0" />
          <span className="text-amber-400 text-[11px] font-semibold uppercase tracking-widest">
            Applications are not open yet.
          </span>
        </div>
      )}
      {/* Application period ended stripe */}
      {!gigExpired && applicationClosed && (
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center gap-1.5 bg-red-950/80 border-b border-red-800/50 px-3 py-1.5 backdrop-blur-sm">
          <FiClock size={11} className="text-red-400 shrink-0" />
          <span className="text-red-400 text-[11px] font-semibold uppercase tracking-widest">
            Application period has ended.
          </span>
        </div>
      )}
      <div
        className={`flex h-full flex-col gap-3 p-5 ${gigExpired || applicationNotYetOpen || applicationClosed ? 'pt-9' : ''}`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="shrink-0">
              {ownerIdentifier ? (
                <Link
                  to="/profile/$identifier"
                  params={{ identifier: ownerIdentifier }}
                  className="block w-12 h-12 rounded-full overflow-hidden border border-zinc-700 hover:border-zinc-500 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="View gig owner profile"
                >
                  <Avatar src={ownerAvatar} alt="Gig owner" name={ownerDisplayName} size="lg" />
                </Link>
              ) : (
                <Avatar
                  src={ownerAvatar}
                  alt="Gig owner"
                  name={ownerDisplayName}
                  size="lg"
                  className="border border-zinc-700"
                />
              )}
            </div>
            <div className="flex flex-col gap-0.5 min-w-0">
              {ownerIdentifier ? (
                <Link
                  to="/profile/$identifier"
                  params={{ identifier: ownerIdentifier }}
                  className="text-white text-[14px] font-semibold hover:text-blue-400 transition-colors truncate"
                  onClick={(e) => e.stopPropagation()}
                >
                  {ownerDisplayName}
                </Link>
              ) : (
                <span className="text-white text-[14px] font-semibold truncate">
                  {ownerDisplayName}
                </span>
              )}
              <p className="text-zinc-500 text-[12px]">Posted {postedTime}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isOwner && onEdit && !gigExpired && (
              <button
                className="p-1.5 hover:bg-zinc-800 rounded-full transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(gig);
                }}
                title="Edit gig"
                aria-label="Edit gig"
              >
                <FiEdit2 size={14} className="text-zinc-500 hover:text-white" />
              </button>
            )}
          </div>
        </div>

        {gigTagLabels.length > 0 && (
          <div className="flex items-center gap-2 overflow-hidden">
            {gigTagLabels.slice(0, 2).map((tag, idx) => (
              <span
                key={`${tag}-${idx}`}
                className="px-3 py-1 rounded-full border border-zinc-700 text-zinc-300 text-[12px] font-medium whitespace-nowrap shrink-0"
              >
                {tag}
              </span>
            ))}
            {gigTagLabels.length > 2 && (
              <span className="px-3 py-1 rounded-full border border-zinc-700 text-zinc-500 text-[12px] whitespace-nowrap shrink-0">
                +{gigTagLabels.length - 2}
              </span>
            )}
          </div>
        )}

        <h3 className="text-[#4fa3e3] font-bold text-[16px] leading-snug truncate min-w-0">
          {gig.title}
        </h3>

        {gig.description && (
          <p className="text-zinc-400 text-[13px] italic leading-relaxed truncate min-w-0">
            {gig.description}
          </p>
        )}

        {gig.event_type && (
          <p className="text-yellow-400 text-[12px] font-semibold truncate min-w-0">
            Needed: {gig.event_type}
          </p>
        )}

        {!gigExpired && (
          <div
            className={`inline-flex w-fit items-center gap-1.5 text-[12px] font-medium px-2.5 py-1 rounded-full border ${getToneClass(statusPresentation.tone)}`}
          >
            <FiCalendar size={11} className="shrink-0" />
            <span>{statusPresentation.label}</span>
          </div>
        )}

        <div className="border-t border-zinc-800/60" />

        {/* Location + Budget row */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <FiMapPin size={14} className="text-zinc-500 shrink-0" />
            <p className="text-zinc-300 text-[13px] truncate font-medium">
              {gig.location_name ||
                [gig.location?.city, gig.location?.state, gig.location?.country]
                  .filter(Boolean)
                  .join(', ') ||
                'Remote'}
            </p>
          </div>
          {budgetDisplay && (
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="text-green-400 text-[13px] font-semibold">{budgetDisplay}</span>
            </div>
          )}
        </div>

        {/* Start date row — compact, always same height */}
        <div className="flex items-center gap-2 min-h-[20px] min-w-0">
          {gig.start_datetime ? (
            <>
              <FiCalendar size={13} className="text-zinc-500 shrink-0" />
              <p className="text-zinc-400 text-[12px] truncate min-w-0">
                {formatDateTime(gig.start_datetime, true)}
              </p>
            </>
          ) : null}
        </div>

        {/* Action row — always rendered, keeps card footer aligned */}
        <div className="mt-auto flex items-center justify-end pt-1">
          {canApply && (
            <div className="flex flex-col items-end gap-1">
              <button
                className="group/apply inline-flex items-center gap-1.5 rounded-full border border-blue-500/40 bg-blue-500/10 px-3 py-1.5 text-[13px] font-semibold text-blue-300 transition-all duration-200 hover:border-blue-400 hover:bg-blue-500/20 hover:text-blue-100 hover:shadow-md hover:shadow-blue-900/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0e0e10] active:scale-[0.98] shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onApply(gig);
                }}
              >
                Apply Now
                <FiArrowRight
                  size={14}
                  className="transition-transform duration-200 group-hover/apply:translate-x-0.5"
                />
              </button>
              <div className="min-h-[16px]">
                {closingLabel && (
                  <p className="text-[11px] text-zinc-500 truncate">{closingLabel}</p>
                )}
              </div>
            </div>
          )}
          {/* Owner: Edit Gig (active) or View Details (expired) */}
          {isOwner && (
            <button
              className={`group/action inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0e0e10] active:scale-[0.98] shrink-0 ${
                gigExpired
                  ? 'border-zinc-600/40 bg-zinc-800/30 text-zinc-400 hover:border-zinc-500 hover:bg-zinc-800/50 hover:text-zinc-300 focus-visible:ring-zinc-400/70'
                  : 'border-amber-500/40 bg-amber-500/10 text-amber-300 hover:border-amber-400 hover:bg-amber-500/20 hover:text-amber-100 hover:shadow-md hover:shadow-amber-900/30 focus-visible:ring-amber-400/70'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                // For both active and expired, clicking always goes to view details
                onViewDetails(gig);
              }}
            >
              {gigExpired ? 'View Details' : 'Edit Gig'}
              <FiArrowRight
                size={14}
                className="transition-transform duration-200 group-hover/action:translate-x-0.5"
              />
            </button>
          )}
          {/* Non-owner, application not yet open */}
          {applicationNotYetOpen && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-700/40 bg-amber-900/20 px-3 py-1.5 text-[13px] font-medium text-amber-400 shrink-0">
              Not Yet Open
            </span>
          )}
          {/* Non-owner, application closed */}
          {applicationClosed && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-red-700/40 bg-red-900/20 px-3 py-1.5 text-[13px] font-medium text-red-400 shrink-0">
              Application Closed
            </span>
          )}
          {/* Non-owner, non-applicable: muted chip */}
          {!canApply && !isOwner && !applicationClosed && !applicationNotYetOpen && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700/50 bg-zinc-800/30 px-3 py-1.5 text-[13px] font-medium text-zinc-500 shrink-0">
              View Details
              <FiArrowRight size={14} />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
